import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import providersService from '../services/providersService';

export const useProfileQuery = (options = {}) => {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => providersService.getProfile(),
    // Approval decisions happen on the admin's machine. The websocket push is
    // the primary signal, but if it was missed (tab asleep, connection
    // dropped), re-checking on focus stops the approval banners from showing
    // stale state until the user reloads the page by hand.
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useExpertiseQuery = (options = {}) => {
  return useQuery({
    queryKey: ['expertise'],
    queryFn: () => providersService.getExpertise(),
    ...options,
  });
};

export const useBanksQuery = (options = {}) => {
  return useQuery({
    queryKey: ['banks'],
    queryFn: () => providersService.getBanks(),
    ...options,
  });
};

export const useUpsertProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.upsertProfile(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useUpdateProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.updateProfile(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useUploadLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (license) => providersService.uploadLicense(license),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useUpdateLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (license) => providersService.updateLicense(license),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useSubmitVehicleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.submitVehicle(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useUpdateVehicleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.updateVehicle(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useSubmitPaymentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.submitPayment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useUpdatePaymentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.updatePayment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useSubmitExpertiseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.submitExpertise(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expertise'] });
    },
  });
};

export const useUpdateExpertiseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => providersService.updateExpertise(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expertise'] });
    },
  });
};

export const useResolveBankAccountQuery = (params, options = {}) => {
  return useQuery({
    queryKey: ['bankResolve', params],
    queryFn: () => providersService.resolveBankAccount(params),
    enabled: !!params?.account_number && !!params?.bank_code,
    ...options,
  });
};

export const useUploadIdDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file) => providersService.uploadIdDocument(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useSubmitProfileReviewMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => providersService.submitProfileReview(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useAvailabilityQuery = (options = {}) => {
  return useQuery({
    queryKey: ['profile', 'availability'],
    queryFn: () => providersService.getAvailability(),
    ...options,
  });
};

export const useUpdateAvailabilityMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isOnline) => providersService.updateAvailability(isOnline),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile', 'availability'] });
    },
  });
};

export const useBankAccountsQuery = (options = {}) => {
  return useQuery({
    queryKey: ['bankAccounts'],
    queryFn: () => providersService.getBankAccounts(),
    ...options,
  });
};

export const useCreateBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => providersService.createBankAccount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useDeleteBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId) => providersService.deleteBankAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
    },
  });
};

export const useSetDefaultBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId) => providersService.setDefaultBankAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useRequestWithdrawalMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => providersService.requestWithdrawal(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useWithdrawalsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['withdrawals', params],
    queryFn: () => providersService.getWithdrawals(params),
    ...options,
  });
};

export const useWithdrawalReceiptQuery = (transactionId, options = {}) => {
  return useQuery({
    queryKey: ['withdrawals', 'receipt', transactionId],
    queryFn: () => providersService.getWithdrawalReceipt(transactionId),
    enabled: !!transactionId,
    ...options,
  });
};
